import React from 'react';
import ChevronUpIcon from './icons/ChevronUpIcon';
import CartIcon from './icons/CartIcon';
import ChartBarIcon from './icons/ChartBarIcon';
import InfoIcon from './icons/InfoIcon';
import { getTranslation } from '../utils/translations';
import { MealType, Language } from '../types';

interface PlannerHeaderProps {
    viewMode: 'week' | 'month';
    setViewMode: (mode: 'week' | 'month') => void;
    
    dateLabel: string;
    onPrevDate: () => void;
    onNextDate: () => void;
    
    isCurrentPeriod: boolean;
    onReturnToCurrent: () => void;
    
    onSyncClick: () => void;
    onInfoClick: () => void;
    
    // Weekly specific
    onMenuClick?: () => void;
    
    language?: Language;
}

const PlannerHeader: React.FC<PlannerHeaderProps> = ({
    viewMode,
    setViewMode,
    dateLabel,
    onPrevDate,
    onNextDate,
    isCurrentPeriod,
    onReturnToCurrent,
    onSyncClick,
    onInfoClick,
    onMenuClick,
    language
}) => {
    const t = (key: string) => getTranslation(language, key);

    return (
        <div className="w-full bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 pb-2 px-4 pt-3 sticky top-0 z-40 shadow-sm flex flex-col transition-colors">
            {/* Row 1: View Toggle & Main Actions */}
            <div className="flex flex-row items-center justify-between w-full mb-3">
                <div className="flex flex-row items-center gap-2 shrink-0">
                    <button 
                        id="planner-sync-button" 
                        onClick={onSyncClick} 
                        className="h-9 w-9 sm:w-auto px-0 sm:px-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex flex-row items-center justify-center gap-1.5 shadow-sm active:scale-95 transition-all shrink-0"
                        title={t('syncShoppingCompact')}
                    >
                        <CartIcon className="w-5 h-5 sm:w-4 sm:h-4 shrink-0" />
                        <span className="hidden sm:inline text-[11px] sm:text-xs font-black uppercase tracking-wide whitespace-nowrap">
                            {t('syncShoppingCompact')}
                        </span>
                    </button>
                    {onMenuClick && (
                        <button 
                            id="planner-stats-button" 
                            onClick={onMenuClick} 
                            className="w-9 h-9 flex items-center justify-center border border-gray-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                            <ChartBarIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>

                <div className="flex flex-row items-center gap-2 shrink-0">
                    <button
                        onClick={onInfoClick}
                        className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full transition-all active:scale-90"
                        title={t('startTour')}
                    >
                        <InfoIcon className="w-4.5 h-4.5" />
                    </button>
                    <div id="planner-view-toggle" className="flex flex-row bg-gray-200/60 dark:bg-slate-800/60 p-1 rounded-lg w-[140px] shadow-inner">
                        <button
                            onClick={() => setViewMode('month')}
                            className={`rounded-md flex-1 py-1 text-center text-[11px] font-black uppercase tracking-wider transition-all duration-200 ${
                                viewMode === 'month' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            {t('monthly')}
                        </button>
                        <button
                            onClick={() => setViewMode('week')}
                            className={`rounded-md flex-1 py-1 text-center text-[11px] font-black uppercase tracking-wider transition-all duration-200 ${
                                viewMode === 'week' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                            }`}
                        >
                            {t('weekly')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Row 2: Date Navigation & "Smart Compass" */}
            <div className={`flex flex-row items-center justify-between w-full ${viewMode === 'week' ? 'mb-4' : 'mb-2'}`}>
                {/* Right side in RTL / Left side in LTR */}
                <div id="planner-date-nav" className="flex flex-row items-center gap-1 sm:gap-2">
                    <button onClick={onPrevDate} className="p-1 sm:p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors active:scale-90">
                        <ChevronUpIcon className="w-5 h-5 ltr:-rotate-90 rtl:rotate-90" />
                    </button>
                    <span className="text-sm sm:text-lg font-bold text-slate-800 dark:text-slate-100 [unicode-bidi:isolate] tracking-tight whitespace-nowrap">{dateLabel}</span>
                    <button onClick={onNextDate} className="p-1 sm:p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors active:scale-90">
                        <ChevronUpIcon className="w-5 h-5 ltr:rotate-90 rtl:-rotate-90" />
                    </button>
                </div>

                {/* Left side in RTL / Right side in LTR */}
                <div id="planner-today-button" className="flex-shrink-0">
                    {isCurrentPeriod ? (
                        <span className="inline-block text-[9px] sm:text-xs font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md uppercase tracking-widest cursor-default select-none whitespace-nowrap shadow-sm">
                            {viewMode === 'week' ? t('weekThis') : t('monthCurrent')}
                        </span>
                    ) : (
                        <button 
                            onClick={onReturnToCurrent}
                            className="text-[9px] sm:text-xs font-bold text-blue-600 bg-blue-50 dark:bg-blue-950/40 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md uppercase tracking-widest shadow-sm hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-all active:scale-95 whitespace-nowrap"
                        >
                            {viewMode === 'week' ? t('returnToThisWeek') : t('returnToThisMonth')}
                        </button>
                    )}
                </div>
            </div>

        </div>
    );
};

export default PlannerHeader;
